import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, FormControl, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { WebhookService } from '../../services/webhook.service';
import { CreateWebhookEventRequest, WebhookDTO, WebhookEventType } from '../../core/models';

interface EventOption {
  value: WebhookEventType;
  label: string;
}

interface EventGroup {
  key: string;
  label: string;
  events: EventOption[];
}

@Component({
  selector: 'app-webhook-form',
  templateUrl: './webhook-form.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    ToggleSwitchModule,
    CheckboxModule
  ]
})
export class WebhookFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private webhookService = inject(WebhookService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  webhookForm: FormGroup;
  loading = signal(false);
  currentWebhook = signal<WebhookDTO | null>(null);
  webhookId = signal<number | null>(null);
  isEdit = computed(() => this.webhookId() != null);

  readonly eventGroups: EventGroup[] = [
    {
      key: 'customer',
      label: 'Clientes',
      events: [
        { value: 'CUSTOMER_CREATED', label: 'customer.created' },
        { value: 'CUSTOMER_UPDATED', label: 'customer.updated' },
        { value: 'CUSTOMER_DELETED', label: 'customer.deleted' }
      ]
    },
    {
      key: 'product',
      label: 'Productos',
      events: [
        { value: 'PRODUCT_CREATED', label: 'product.created' },
        { value: 'PRODUCT_UPDATED', label: 'product.updated' },
        { value: 'PRODUCT_DELETED', label: 'product.deleted' }
      ]
    },
    {
      key: 'sale',
      label: 'Ventas',
      events: [
        { value: 'SALE_CREATED', label: 'sale.created' },
        { value: 'SALE_UPDATED', label: 'sale.updated' },
        { value: 'SALE_DELETED', label: 'sale.deleted' }
      ]
    },
    {
      key: 'payment',
      label: 'Pagos',
      events: [
        { value: 'PAYMENT_CREATED', label: 'payment.created' }
      ]
    },
    {
      key: 'subscription',
      label: 'Suscripciones',
      events: [
        { value: 'SUBSCRIPTION_CREATED', label: 'subscription.created' },
        { value: 'SUBSCRIPTION_UPDATED', label: 'subscription.updated' },
        { value: 'SUBSCRIPTION_DELETED', label: 'subscription.deleted' }
      ]
    },
    {
      key: 'invoice',
      label: 'Facturas',
      events: [
        { value: 'INVOICE_CREATED', label: 'invoice.created' },
        { value: 'INVOICE_UPDATED', label: 'invoice.updated' }
      ]
    },
    {
      key: 'charge',
      label: 'Cargos',
      events: [
        { value: 'CHARGE_OVERDUE', label: 'charge.overdue' },
        { value: 'CHARGE_DELINQUENT', label: 'charge.delinquent' }
      ]
    },
    {
      key: 'reconciliation',
      label: 'Conciliación',
      events: [
        { value: 'RECONCILIATION_CREATED', label: 'reconciliation.created' }
      ]
    },
    {
      key: 'mit',
      label: 'Cobros bajo demanda',
      events: [
        { value: 'MIT_MANDATE_CREATED', label: 'mit_mandate.created' },
        { value: 'MIT_MANDATE_UPDATED', label: 'mit_mandate.updated' },
        { value: 'MIT_MANDATE_CARD_EXPIRING', label: 'mit_mandate.card_expiring' },
        { value: 'MIT_TRANSACTION_CREATED', label: 'mit_transaction.created' },
        { value: 'MIT_TRANSACTION_UPDATED', label: 'mit_transaction.updated' }
      ]
    }
  ];

  constructor() {
    this.webhookForm = this.fb.group({
      endpoint: ['', [Validators.required]],
      description: [''],
      enabled: [true],
      headers: this.fb.array([]),
      subscribedEvents: this.fb.control<WebhookEventType[]>([], [Validators.required, Validators.minLength(1)])
    });
  }

  get headersArray(): FormArray {
    return this.webhookForm.get('headers') as FormArray;
  }

  get subscribedEventsControl(): FormControl<WebhookEventType[]> {
    return this.webhookForm.get('subscribedEvents') as FormControl<WebhookEventType[]>;
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      const id = Number(idParam);
      if (!Number.isNaN(id)) {
        this.webhookId.set(id);
        this.loadWebhook(id);
        return;
      }
    }
  }

  goBack(): void {
    this.router.navigate(['/webhooks']);
  }

  loadWebhook(id: number): void {
    this.loading.set(true);
    this.webhookService.getWebhook(id)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.currentWebhook.set(res.body);
            this.patchForm(res.body);
          } else {
            this.toastr.error(res.message || 'Webhook no encontrado');
            this.goBack();
          }
        },
        error: () => {
          this.toastr.error('Webhook no encontrado');
          this.goBack();
        }
      });
  }

  patchForm(webhook: WebhookDTO): void {
    this.webhookForm.patchValue({
      endpoint: webhook.endpoint,
      description: webhook.description,
      enabled: webhook.enabled ?? false,
      subscribedEvents: webhook.subscribedEvents ?? []
    });
    this.headersArray.clear();
    if (webhook.headers) {
      Object.entries(webhook.headers).forEach(([key, value]) => {
        this.addHeader(key, value);
      });
    }
  }

  isEventSelected(eventType: WebhookEventType): boolean {
    return this.subscribedEventsControl.value?.includes(eventType) ?? false;
  }

  toggleEvent(eventType: WebhookEventType, checked: boolean): void {
    const current = [...(this.subscribedEventsControl.value ?? [])];
    const idx = current.indexOf(eventType);
    if (checked && idx < 0) {
      current.push(eventType);
    } else if (!checked && idx >= 0) {
      current.splice(idx, 1);
    }
    this.subscribedEventsControl.setValue(current);
    this.subscribedEventsControl.markAsTouched();
  }

  toggleGroup(group: EventGroup, checked: boolean): void {
    const current = new Set(this.subscribedEventsControl.value ?? []);
    for (const ev of group.events) {
      if (checked) current.add(ev.value);
      else current.delete(ev.value);
    }
    this.subscribedEventsControl.setValue([...current]);
    this.subscribedEventsControl.markAsTouched();
  }

  isGroupFullySelected(group: EventGroup): boolean {
    const selected = new Set(this.subscribedEventsControl.value ?? []);
    return group.events.every((e) => selected.has(e.value));
  }

  isGroupPartiallySelected(group: EventGroup): boolean {
    const selected = new Set(this.subscribedEventsControl.value ?? []);
    const count = group.events.filter((e) => selected.has(e.value)).length;
    return count > 0 && count < group.events.length;
  }

  addHeader(key: string = '', value: string = ''): void {
    this.headersArray.push(this.fb.group({
      key: [key, Validators.required],
      value: [value, Validators.required]
    }));
  }

  removeHeader(index: number): void {
    this.headersArray.removeAt(index);
  }

  copySecret(): void {
    const secret = this.currentWebhook()?.secret;
    if (!secret) return;
    navigator.clipboard.writeText(secret).then(() => {
      this.toastr.success('Secret copiado al portapapeles');
    });
  }

  onSubmit(): void {
    if (this.webhookForm.invalid) {
      this.webhookForm.markAllAsTouched();
      if (!this.subscribedEventsControl.value?.length) {
        this.toastr.warning('Selecciona al menos un evento');
      }
      return;
    }

    const formValue = this.webhookForm.value;
    const headersMap: Record<string, string> = {};
    formValue.headers.forEach((h: { key: string; value: string }) => {
      headersMap[h.key] = h.value;
    });

    const request: CreateWebhookEventRequest = {
      endpoint: formValue.endpoint,
      description: formValue.description,
      enabled: formValue.enabled === true,
      headers: headersMap,
      subscribedEvents: formValue.subscribedEvents ?? []
    };

    this.loading.set(true);
    const id = this.webhookId();
    const request$ = id != null
      ? this.webhookService.updateWebhook(id, request)
      : this.webhookService.createWebhook(request);

    request$
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.toastr.success(id != null ? 'Webhook actualizado' : 'Webhook creado');
            this.currentWebhook.set(res.body);
            if (id == null && res.body.id != null) {
              this.router.navigate(['/webhooks', res.body.id], { replaceUrl: true });
              this.webhookId.set(res.body.id);
            }
          } else {
            this.toastr.error(res.message || 'Error al guardar webhook');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Error al conectar con el servidor');
        }
      });
  }
}
